import { useState, Fragment } from 'react'
import i18next from 'i18next'
import take from 'lodash/take'
import isNil from 'lodash/isNil'
import cx from 'classnames'

import { Link } from 'features/i18n/router'
import { fromNowToFullDate } from 'lib/time'
import { Post } from 'lib/_types'
import { logEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { useCurrentUser } from 'store/api/yay/user'

import Avatar from './Avatar'
import ConfirmBox from './ConfirmBox'
import Nickname from './Nickname'
import { useNavigate } from 'react-router-dom'

const prepareMentions = (post: Post) => {
  const { data: currentUser } = useCurrentUser()

  return take(
    (post?.mentions ?? []).filter((mention) => {
      return mention.id !== currentUser?.id && mention.id !== post?.user?.id
    }),
    4
  )
}

const ReplyTo = ({
  post,
  repliedPost,
  chooseRepliers,
  postLink,
  hideSingleMention = false,
  noLink,
  isFromTimeline = false,
  onSavePreloadedPosts,
}: {
  post: Post
  repliedPost: Partial<Post>
  chooseRepliers?: boolean
  postLink?: string
  hideSingleMention?: boolean
  noLink?: boolean
  isFromTimeline?: boolean
  onSavePreloadedPosts?: Function
}) => {
  const navigate = useNavigate()

  const [__mentions, __mentionsSet] = useState(prepareMentions(post))
  const [__isActive, __isActiveSet] = useState(false)

  const onToggleConfirmBox = () => {
    __isActiveSet((prevState) => !prevState)
  }

  const onToggleMentionUser = (mention: any) => {
    if (__mentions?.find?.((_mention) => mention.id === _mention.id)) {
      __mentionsSet((prevState) => {
        return prevState?.filter?.((_mention) => mention.id !== _mention.id)
      })
    } else {
      __mentionsSet((prevState) => {
        return [...prevState, mention]
      })
    }
  }

  const op = post?.user
  const mentions = prepareMentions(post) // State may change, but may not the display, thus use data from props here.

  const link = (() => {
    if (noLink) return undefined
    if (postLink) return postLink
    if (isNil(post?.conversation_id)) return undefined

    return post?.group_id
      ? ROUTE_PATH.GROUP_CONVERSATION(post?.group_id, post?.conversation_id)
      : ROUTE_PATH.CONVERSATION(post?.conversation_id)
  })()

  const Handler = (isNil(link) ? 'a' : Link) as any
  const mentionsWithoutOp = __mentions?.filter?.(
    (mention) => mention.id !== repliedPost?.user?.id
  )

  if (mentionsWithoutOp?.length == 0 && hideSingleMention) {
    return null
  }

  return (
    <Fragment>
      <div className="ReplyTo">
        <div className="ReplyTo__handle">
          {(() => {
            let mentionText = repliedPost?.user?.nickname

            if (mentionsWithoutOp.length > 0) {
              mentionText = i18next.t('reply_to.mention_list', {
                mentionText,
                total: mentionsWithoutOp.length,
              })
            }

            return (
              <Handler
                className={cx('ReplyTo__handle__a', {
                  'ReplyTo__handle__a--no-link': noLink,
                })}
                onClick={(e: Event) => {
                  if (chooseRepliers) {
                    e.preventDefault()
                    e.stopPropagation()
                    onToggleConfirmBox()

                    // the current location is the main timeline / or a circle timeline /group/GROUP_ID
                    if (isFromTimeline && onSavePreloadedPosts) {
                      console.log('PostItem: requesting save preloaded posts')
                      onSavePreloadedPosts()
                    }

                    navigate(link)
                  }
                }}
              >
                {isNil(repliedPost) ? (
                  <span className="ReplyTo__handle__a__text">
                    {i18next.t('reply_to.post_reply_target_null')}
                  </span>
                ) : (
                  <span className="ReplyTo__handle__a__text">
                    {i18next.t('reply_to.re')}:{' '}
                    <span className="ReplyTo__handle__a__text__span">
                      {mentionText}
                    </span>
                  </span>
                )}
              </Handler>
            )
          })()}

          {chooseRepliers ? (
            <input
              type="hidden"
              name="mention_ids"
              value={[...__mentions, op].map((mention) => mention.id).join(',')}
            />
          ) : (
            <Fragment>
              {!isNil(repliedPost) && (
                <div className="ReplyTo__handle__panel">
                  <div className="ReplyTo__handle__panel__wrapper">
                    <Link
                      onClick={() =>
                        logEvent.user_profile_open({
                          target_user_id: repliedPost?.user?.id.toString(),
                          from: 'undefined',
                        })
                      }
                      to={ROUTE_PATH.USER(repliedPost?.user?.id)}
                    >
                      <Avatar user={repliedPost?.user} />
                    </Link>

                    <h5 className="ReplyTo__handle__panel__h5">
                      <Link
                        onClick={() =>
                          logEvent.user_profile_open({
                            target_user_id: repliedPost?.user?.id.toString(),
                            from: 'undefined',
                          })
                        }
                        to={ROUTE_PATH.USER(repliedPost?.user?.id)}
                      >
                        {repliedPost?.user?.nickname}
                      </Link>
                    </h5>

                    <time className="ReplyTo__handle__panel__time">
                      {fromNowToFullDate(repliedPost?.created_at * 1000)}
                    </time>

                    <p className="ReplyTo__handle__panel__p">
                      {repliedPost?.text}
                    </p>
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>

      {chooseRepliers && (
        <ConfirmBox isActive={__isActive}>
          <a className="ConfirmBox__overlay" onClick={onToggleConfirmBox} />

          <div className="ConfirmBox__wrapper">
            <div className="ConfirmBox__header">
              <h3 className="ConfirmBox__header__h3">
                {i18next.t('reply_to.repliers_box.title')}
              </h3>

              <a
                className="ConfirmBox__header__close"
                onClick={onToggleConfirmBox}
              />
            </div>

            <dl className="ConfirmBox__users ConfirmBox__users--mentions">
              <div className="ConfirmBox__users__item ConfirmBox__users__item--no-bdb">
                <Avatar user={op} />

                <dt>
                  <Nickname user={op} />
                </dt>
              </div>

              {mentions.length > 0 && (
                <Fragment>
                  <p className="ConfirmBox__users__p">
                    {i18next.t('reply_to.repliers_box.mention_list')}
                  </p>

                  {mentions.map((mention) => (
                    <a className="ConfirmBox__users__item" key={mention.id}>
                      <Avatar user={mention} />

                      <dt>
                        <Nickname user={mention} />
                      </dt>

                      <label className="CheckBox">
                        <input
                          type="checkbox"
                          onChange={() => onToggleMentionUser(mention)}
                          checked={
                            typeof (__mentions ?? []).find(
                              ({ id }) => id === mention.id
                            ) !== 'undefined'
                          }
                        />
                        <span />
                      </label>
                    </a>
                  ))}
                </Fragment>
              )}
            </dl>
          </div>
        </ConfirmBox>
      )}
    </Fragment>
  )
}

export default ReplyTo
